import React from 'react'
import { Space } from 'antd'
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled'
import InfoCircleFilled from '@ant-design/icons/InfoCircleFilled'
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled'

import classNames from 'classnames'
import styles from './index.less'

/**
 * 进度条成功
 * @component
 * @param {object} props - React props。
 * @param {string} props.title - 标题文本。
 * @param {string} props.text - 文本内容。
 * @param {string} props.size - 尺寸，可选值为 'small', 'default', 'large'。
 * @param {object} props.spaceProp - Space 组件的属性。
 * @returns {React.Node} - React component.
 */
export const SuccessTitle = ({
    title,
    text,
    size = 'default',
    spaceProp = { direction: 'vertical', align: 'center', size: 4 },
}) => {
    return (
        <Space
            className={classNames({
                [styles.smallTitle]: size === 'small',
                [styles.defaultTitle]: size === 'default',
                [styles.largeTitle]: size === 'large',
            })}
            {...spaceProp}
        >
            <div className={styles.icon}>
                <CheckCircleFilled style={{ color: '#22b001' }} />
            </div>
            {title && <div className={styles.title}>{title}</div>}
            {text && <div className={styles.text}>{text}</div>}
        </Space>
    )
}

/**
 * 进度条失败
 * @component
 * @param {object} props - React props。
 * @param {string} props.title - 标题文本。
 * @param {string} props.text - 文本内容。
 * @param {string} props.size - 尺寸，可选值为 'small', 'default', 'large'。
 * @param {object} props.spaceProp - Space 组件的属性。
 * @returns {React.Node} - React component.
 */
export const FailTitle = ({
    title,
    text,
    size = 'default',
    spaceProp = { direction: 'vertical', align: 'center', size: 4 },
}) => {
    return (
        <Space
            className={classNames({
                [styles.smallTitle]: size === 'small',
                [styles.defaultTitle]: size === 'default',
                [styles.largeTitle]: size === 'large',
            })}
            {...spaceProp}
        >
            <div className={styles.icon}>
                <InfoCircleFilled style={{ color: '#f04851' }} />
            </div>
            {title && <div className={styles.title}>{title}</div>}
            {text && <div className={styles.text}>{text}</div>}
        </Space>
    )
}

/**
 * 进度条数量显示组件
 * @component
 * @param {object} props - React props。
 * @param {string} props.size - 尺寸，可选值为 'small', 'default'。
 * @param {object} props.dataSource - 数据源对象。
 * @param {object} props.spaceProp - Space 组件的属性。
 * @param {string} props.numTip - 数量的单位提示。
 * @param {string} props.className - 组件的类名。
 * @returns {React.Node} - React component.
 */
export const ResultsNum = ({ size = 'default', dataSource, spaceProp = { size: 18 }, numTip = '款', className }) => {
    const totalQty = dataSource?.totalQty
    const successQty = dataSource?.successQty
    const failQty = dataSource?.failQty

    return (
        <div className={className}>
            <Space
                className={classNames({
                    [styles.smallResultsNum]: size === 'small',
                    [styles.defaultResultsNum]: size === 'default',
                })}
                {...spaceProp}
            >
                {String(totalQty) !== 'undefined' && (
                    <div className={styles.item}>
                        总数：
                        <span className={styles.value}>
                            <span className={styles.num}>{totalQty}</span>
                            {numTip}
                        </span>
                    </div>
                )}
                {String(successQty) && (
                    <div className={styles.item}>
                        成功：
                        <span className={styles.value}>
                            <span style={{ color: '#22b001' }} className={styles.num}>
                                {successQty}
                            </span>
                            {numTip}
                        </span>
                    </div>
                )}
                {String(failQty) && (
                    <div className={styles.item}>
                        失败：
                        <span className={styles.value}>
                            <span style={{ color: '#f04851' }} className={styles.num}>
                                {failQty}
                            </span>
                            {numTip}
                        </span>
                    </div>
                )}
            </Space>
        </div>
    )
}

/**
 * 进度条，包括成功、部分成功、失败状态。
 * @component
 * @param {object} props - React props。
 * @param {string} props.status - 状态，可选值为 'success', 'partSuccess', 'fail'。
 * @param {string} props.title - 标题文本。
 * @param {string} props.text - 文本内容。
 * @param {object} props.spaceProp - Space 组件的属性。
 * @returns {React.Node} - React component.
 */
export const StatusTitle = ({
    status,
    title,
    text,
    spaceProp = { direction: 'horizontal', align: 'center', size: 8 },
}) => {
    return (
        <Space className={styles.statusTitle} {...spaceProp}>
            <div className={styles.icon}>
                {status === 'success' && <CheckCircleFilled style={{ color: '#22b001' }} />}
                {status === 'partSuccess' && <InfoCircleFilled style={{ color: '#1890FF' }} />}
                {status === 'fail' && <CloseCircleFilled style={{ color: '#f04851' }} />}
            </div>
            {title && <div className={styles.title}>{title}</div>}
            {text && <div className={styles.text}>{text}</div>}
        </Space>
    )
}

/**
 * 数量显示V2 组件，带有失败数为0时不标红的特性。
 * @component
 * @param {object} props - React props。
 * @param {object} props.dataSource - 数据源对象。
 * @param {object} props.spaceProp - Space 组件的属性。
 * @param {string} props.numTip - 数量的单位提示。
 * @returns {React.Node} - React component.
 */
export const ResultsNumV2 = ({ dataSource, spaceProp = { size: 28 }, numTip = '款' }) => {
    const totalQty = dataSource?.totalQty
    const successQty = dataSource?.successQty
    const failQty = dataSource?.failQty

    return (
        <div>
            <Space className={styles.defaultResultsNumV2} {...spaceProp}>
                {String(totalQty) && (
                    <div className={styles.item}>
                        总数：
                        <span className={styles.value}>
                            <span className={styles.num}>{totalQty}</span>
                            {numTip}
                        </span>
                    </div>
                )}
                {String(successQty) && (
                    <div className={styles.item}>
                        成功：
                        <span className={styles.value}>
                            <span style={successQty > 0 ? { color: '#22b001' } : {}} className={styles.num}>
                                {successQty}
                            </span>
                            {numTip}
                        </span>
                    </div>
                )}
                {String(failQty) && (
                    <div className={styles.item}>
                        失败：
                        <span className={styles.value}>
                            <span style={failQty > 0 ? { color: '#f04851' } : {}} className={styles.num}>
                                {failQty}
                            </span>
                            {numTip}
                        </span>
                    </div>
                )}
            </Space>
        </div>
    )
}
